import AlertMinimalIcon from '@assets/icons/alert-minimal.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { getAddedVariantPackEventPayloads } from '@/utils/ads';
import { ItemListOption } from '@/contexts/item-context-types';
import {
  QuantitySelector,
  Props as QuantitySelectorProps,
} from './quantity-selector';
import { StyleProp, Text, TextStyle, View, ViewStyle } from 'react-native';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';

interface MultiSkuButtonRendererArgs {
  onSelect: () => void;
  variantPackUUID: string;
}

interface MinimalOrderableItemActionButtonProps {
  availabilityStatusEnabled: boolean;
  isAddToOrderDisabled: boolean;
  isAddToOrderPrebookItemDisabled: boolean;
  isEssential: boolean;
  isProspect: boolean;
  metadata: NonNullable<ItemListOption['metadata']>;
  onSelect: () => void;
  quantity: number;
  quantityPillStyle?: StyleProp<TextStyle>;
  quantitySelectorProps?: Partial<QuantitySelectorProps>;
  renderAsSmall: boolean;
  renderMultiSkuButton: (args: MultiSkuButtonRendererArgs) => React.ReactNode;
  unavailableTextStyle: StyleProp<TextStyle>;
  unavailableWrapperStyle: StyleProp<ViewStyle>;
  updateQuantity: UpdateQuantityFn;
  variantPackUUID: string;
}

export const MinimalOrderableItemActionButton: React.FC<
  MinimalOrderableItemActionButtonProps
> = ({
  availabilityStatusEnabled,
  isAddToOrderDisabled,
  isAddToOrderPrebookItemDisabled,
  isEssential,
  isProspect,
  metadata,
  onSelect,
  quantity,
  quantityPillStyle,
  quantitySelectorProps,
  renderAsSmall,
  renderMultiSkuButton,
  unavailableTextStyle,
  unavailableWrapperStyle,
  updateQuantity,
  variantPackUUID,
}) => {
  if (metadata.skus.length > 1) {
    return renderMultiSkuButton({ onSelect, variantPackUUID });
  }

  if (isAddToOrderDisabled || isAddToOrderPrebookItemDisabled) {
    if (availabilityStatusEnabled) {
      return null;
    }

    return (
      <View style={unavailableWrapperStyle}>
        <Text style={unavailableTextStyle}>{i18n.t('unavailable')}</Text>
      </View>
    );
  }

  if (isProspect) {
    return null;
  }

  return (
    <QuantitySelector
      additionalIcon={isEssential ? AlertMinimalIcon : null}
      isFocused={false}
      maxQuantity={metadata.maximumQuantity}
      minQuantity={metadata.minimumQuantity}
      onChange={(previousQuantity: number, newQuantity: number) => {
        updateQuantity({
          eventPayloads: getAddedVariantPackEventPayloads(metadata),
          newQuantity,
          previousQuantity,
          variantPackDetails: {
            itemDisplayName: metadata.displayName,
            metadata: metadata.variantPackMetadata,
          },
          variantPackUUID,
        });
      }}
      quantity={quantity}
      quantityPillStyle={renderAsSmall ? quantityPillStyle : null}
      {...quantitySelectorProps}
    />
  );
};
